import React, { useEffect } from 'react';
import { useAccount, useChainId, useSwitchChain } from 'wagmi';
import { formatUnits } from 'viem';
import { AlertCircle } from 'lucide-react';
import { colors, spacing, typography } from '../../../tokens';
import { PageTitle, GradientText } from '../../../atoms';
import { ConnectButton } from '../../../molecules/web3/ConnectButton';
import { useAuth } from '../../../web3/hooks/useAuth';
import {
  SourceNetworkSelector,
  BridgeForm,
  BridgeStatus,
  NetworkBalances,
  ConnectionSteps
} from './components';
import {
  useFormState,
  useContractReads,
  useBridgeActions,
  useQuoteFees,
  useMultiChainBalances
} from './hooks';
import { getNetworkConfig, BRIDGE_NETWORKS } from './constants';

/**
 * Bridgepage - Cross-chain token bridge from Polygon/Ethereum to BSC
 * One-way bridge using LayerZero protocol
 *
 * User Flow:
 * 1. Connect wallet
 * 2. Switch to source network (Polygon or Ethereum)
 * 3. Sign message (SIWE authentication)
 * 4. View balances from all networks
 * 5. Select amount and bridge to BSC
 */
const Bridgepage: React.FC = () => {
  const { address, isConnected } = useAccount();
  const currentChainId = useChainId();
  const { switchChain } = useSwitchChain();
  const { isAuthenticated } = useAuth();

  // Form state management
  const formState = useFormState();

  // Fetch balances from all networks
  const multiChainBalances = useMultiChainBalances({ address });

  // Contract reads (balance, allowance, decimals) for current source chain
  const contractData = useContractReads({
    address,
    sourceChainId: formState.sourceChainId,
  });

  // Fee quoting
  const feeQuote = useQuoteFees({
    adapterAddress: contractData.adapterAddress,
    userAddress: address,
    sourceChainId: formState.sourceChainId,
    amount: formState.amount,
    decimals: contractData.decimals,
  });

  // Bridge actions (approve, bridge)
  const bridgeActions = useBridgeActions({
    tokenAddress: contractData.tokenAddress,
    adapterAddress: contractData.adapterAddress,
    sourceChainId: formState.sourceChainId,
    userAddress: address,
    decimals: contractData.decimals,
    refetchBalance: () => {
      contractData.refetchBalance();
      multiChainBalances.refetch();
    },
    refetchAllowance: contractData.refetchAllowance,
    clearForms: formState.clearForms,
  });

  // Check if current network is a valid source network
  const isValidSourceNetwork = BRIDGE_NETWORKS.some(
    (network) => network.chainId === currentChainId
  );

  // Determine current step
  // Step 1: Not connected
  // Step 2: Wrong network (not Polygon or Ethereum)
  // Step 3: Not authenticated (need to sign message)
  // Step 4: Ready to bridge
  const currentStep = !isConnected
    ? 1
    : !isValidSourceNetwork
    ? 2
    : !isAuthenticated
    ? 3
    : 4;

  // Note: We do NOT auto-switch when user selects source network in dropdown
  // This prevents flickering and gives user control
  // User must click "Switch to [Network]" button explicitly

  // Reset fee quote when amount or source chain changes
  useEffect(() => {
    feeQuote.resetQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.amount, formState.sourceChainId]);

  // Handle max button click
  const handleMaxClick = () => {
    if (contractData.balance) {
      const maxBalance = formatUnits(contractData.balance, contractData.decimals);
      // Truncate to 6 decimals (bridge constraint)
      const truncated = maxBalance.split('.').length > 1
        ? maxBalance.split('.')[0] + '.' + maxBalance.split('.')[1].slice(0, 6)
        : maxBalance;
      formState.setMaxAmount(truncated);
    }
  };

  // Handle bridge execution
  const handleBridge = () => {
    bridgeActions.handleBridge(formState.amount, feeQuote.nativeFee, feeQuote.lzTokenFee);
  };

  // Get network names for display
  const sourceNetwork = getNetworkConfig(formState.sourceChainId);

  // Check if user is on the correct network
  const isOnCorrectNetwork = currentChainId === formState.sourceChainId;

  return (
    <div
      style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: spacing['2xl'],
      }}
    >
      {/* Page Header */}
      <div
        style={{
          textAlign: 'center',
          marginBottom: spacing.xl,
        }}
      >
        <PageTitle style={{ marginBottom: spacing.sm }}>
          Bridge <GradientText>FAR Tokens</GradientText>
        </PageTitle>
        <p
          style={{
            fontSize: typography.fontSize.base,
            color: colors.text.secondary.default,
          }}
        >
          Cross-chain bridge powered by LayerZero
        </p>
      </div>

      {/* Connection Steps Progress */}
      <ConnectionSteps currentStep={currentStep} />

      {/* Step 1: Connect Wallet */}
      {currentStep === 1 && (
        <div
          style={{
            textAlign: 'center',
            padding: spacing.xl,
            backgroundColor: colors.background.secondary.default,
            borderRadius: '20px',
            border: `1px solid ${colors.border.default.default}`,
          }}
        >
          <AlertCircle size={48} color={colors.brand.cyan.base} style={{ marginBottom: spacing.md }} />
          <h3
            style={{
              fontSize: typography.fontSize.xl,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text.primary.default,
              margin: `0 0 ${spacing.sm} 0`,
            }}
          >
            Connect Your Wallet
          </h3>
          <p
            style={{
              fontSize: typography.fontSize.base,
              color: colors.text.secondary.default,
              marginBottom: spacing.lg,
            }}
          >
            Connect your wallet to start bridging FAR tokens across chains
          </p>
          <ConnectButton size="lg" />
        </div>
      )}

      {/* Step 2: Switch to Valid Source Network */}
      {currentStep === 2 && (
        <div
          style={{
            textAlign: 'center',
            padding: spacing.xl,
            backgroundColor: colors.background.secondary.default,
            borderRadius: '20px',
            border: `1px solid ${colors.semantic.warning}`,
          }}
        >
          <AlertCircle size={48} color={colors.semantic.warning} style={{ marginBottom: spacing.md }} />
          <h3
            style={{
              fontSize: typography.fontSize.xl,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text.primary.default,
              margin: `0 0 ${spacing.sm} 0`,
            }}
          >
            Switch to Source Network
          </h3>
          <p
            style={{
              fontSize: typography.fontSize.base,
              color: colors.text.secondary.default,
              marginBottom: spacing.lg,
            }}
          >
            Please switch to Polygon or Ethereum to bridge tokens
          </p>
          <div style={{ display: 'flex', gap: spacing.md, justifyContent: 'center' }}>
            {BRIDGE_NETWORKS.map((network) => (
              <button
                key={network.chainId}
                onClick={() => switchChain?.({ chainId: network.chainId })}
                style={{
                  padding: `${spacing.md} ${spacing.xl}`,
                  backgroundColor: colors.brand.primary.base,
                  color: colors.text.primary.default,
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: typography.fontSize.base,
                  fontWeight: typography.fontWeight.semibold,
                  cursor: 'pointer',
                }}
              >
                Switch to {network.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Sign Message (Authentication) */}
      {currentStep === 3 && (
        <div
          style={{
            textAlign: 'center',
            padding: spacing.xl,
            backgroundColor: colors.background.secondary.default,
            borderRadius: '20px',
            border: `1px solid ${colors.border.default.default}`,
          }}
        >
          <AlertCircle size={48} color={colors.brand.blue.base} style={{ marginBottom: spacing.md }} />
          <h3
            style={{
              fontSize: typography.fontSize.xl,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text.primary.default,
              margin: `0 0 ${spacing.sm} 0`,
            }}
          >
            Sign Message to Continue
          </h3>
          <p
            style={{
              fontSize: typography.fontSize.base,
              color: colors.text.secondary.default,
              marginBottom: spacing.lg,
            }}
          >
            Sign a message to verify wallet ownership and unlock bridge functionality
          </p>
          <ConnectButton size="lg" requireAuth />
        </div>
      )}

      {/* Step 4: Ready to Bridge */}
      {currentStep === 4 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.lg,
          }}
        >
          {/* Multi-Chain Balance Display */}
          <NetworkBalances
            polygonBalance={multiChainBalances.polygonBalance}
            bscBalance={multiChainBalances.bscBalance}
            ethereumBalance={multiChainBalances.ethereumBalance}
            decimals={multiChainBalances.decimals}
            isLoading={multiChainBalances.isLoading}
            currentChainId={currentChainId}
          />

          {/* Network Mismatch Warning */}
          {!isOnCorrectNetwork && (
            <div
              style={{
                padding: spacing.lg,
                backgroundColor: colors.semantic.warning,
                color: colors.background.primary.default,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.sm,
                alignItems: 'center',
              }}
            >
              <div style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold }}>
                Wrong Network Detected
              </div>
              <div style={{ fontSize: typography.fontSize.sm }}>
                Please switch to {sourceNetwork?.name} network to continue
              </div>
              <button
                onClick={() => switchChain?.({ chainId: formState.sourceChainId })}
                style={{
                  padding: `${spacing.sm} ${spacing.lg}`,
                  backgroundColor: colors.background.primary.default,
                  color: colors.text.primary.default,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  cursor: 'pointer',
                  marginTop: spacing.xs,
                }}
              >
                Switch to {sourceNetwork?.name}
              </button>
            </div>
          )}

          {/* Source Network Selector */}
          <SourceNetworkSelector
            selectedChainId={formState.sourceChainId}
            onSelect={formState.setSourceChainId}
            disabled={bridgeActions.isBridgePending || bridgeActions.isBridgeLoading}
          />

          {/* Bridge Form */}
          <BridgeForm
            amount={formState.amount}
            onAmountChange={formState.setAmount}
            onMaxClick={handleMaxClick}
            balance={contractData.balance}
            allowance={contractData.allowance}
            decimals={contractData.decimals}
            isBalanceLoading={contractData.isBalanceLoading}
            nativeFee={feeQuote.nativeFee}
            lzTokenFee={feeQuote.lzTokenFee}
            isFetchingFee={feeQuote.isFetchingFee}
            isFeeQuoted={feeQuote.isFeeQuoted}
            feeError={feeQuote.feeError}
            onQuoteFees={feeQuote.quoteFees}
            onApprove={() => bridgeActions.handleApprove(formState.amount)}
            onBridge={handleBridge}
            isApprovePending={bridgeActions.isApprovePending}
            isApproveLoading={bridgeActions.isApproveLoading}
            isBridgePending={bridgeActions.isBridgePending}
            isBridgeLoading={bridgeActions.isBridgeLoading}
            isOnCorrectNetwork={isOnCorrectNetwork}
          />

          {/* Bridge Status */}
          <BridgeStatus
            isBridgeSuccess={bridgeActions.isBridgeSuccess}
            isBridgeLoading={bridgeActions.isBridgeLoading}
            bridgeHash={bridgeActions.bridgeHash}
            bridgeError={bridgeActions.bridgeError}
          />

          {/* Information */}
          <div
            style={{
              padding: spacing.md,
              backgroundColor: colors.background.secondary.default,
              borderRadius: '12px',
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
            }}
          >
            <p style={{ marginBottom: spacing.sm }}>
              <strong style={{ color: colors.text.secondary.default }}>How it works:</strong>
            </p>
            <ol style={{ paddingLeft: spacing.lg, margin: 0 }}>
              <li style={{ marginBottom: spacing.xs }}>Select source network (Polygon or Ethereum)</li>
              <li style={{ marginBottom: spacing.xs }}>Enter amount to bridge</li>
              <li style={{ marginBottom: spacing.xs }}>Approve FAR tokens (if needed)</li>
              <li style={{ marginBottom: spacing.xs }}>Get bridge fees</li>
              <li>Confirm bridge transaction</li>
            </ol>
            <p style={{ marginTop: spacing.md, fontSize: typography.fontSize.xs }}>
              Powered by LayerZero - Cross-chain transactions may take a few minutes to complete
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bridgepage;
